import React, { useState } from "react";
import { ADVISORS, FOUNDER, BRAND } from "@/lib/content";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ZoomIn, ArrowLeft, X, Lock } from "lucide-react";

const initialsOf = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const cedulaLabel = (c) =>
  c === "B" ? "Cédula B" : c === "A1" ? "Cédula A1" : "Cédula A";

const Advisors = () => {
  const [selected, setSelected] = useState(null);
  const closeDialog = () => setSelected(null);
  return (
    <section
      id="asesores"
      data-testid="advisors-section"
      className="relative bg-[#0B0410] text-[#F9F6F0] py-24 lg:py-32 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute top-1/2 -translate-y-1/2 right-0 w-[700px] h-[700px] rounded-full bg-[#3D1565]/10 blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#E6D5B8]" />
              <span
                className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] font-medium"
                data-testid="advisors-eyebrow"
              >
                El equipo
              </span>
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.08]"
              data-testid="advisors-headline"
            >
              <span className="italic text-[#E6D5B8]">Once asesores</span>{" "}
              certificados, una misma filosofía.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base lg:text-lg text-[#F9F6F0]/70 font-light">
              Cada uno de nuestros asesores está certificado ante la CNSF con
              cédula vigente (A, A1 o B). Detrás de cada cliente hay un
              profesional dedicado y, cuando se requiere, todo el equipo.
            </p>
          </div>
        </div>

        {/* Founder card — featured */}
        <article
          data-testid="founder-card"
          className="relative bg-gradient-to-br from-[#150A1F] to-[#0B0410] border border-[#3D1565] rounded-sm p-8 lg:p-12 mb-12 overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#3D1565]/15 to-transparent pointer-events-none" />
          <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-4 flex items-center gap-5">
              <div className="relative w-full max-w-[260px] aspect-square overflow-hidden rounded-sm border border-[#3D1565]">
                <img
                  src={FOUNDER.photo}
                  alt={FOUNDER.name}
                  data-testid="founder-photo"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0410]/40 via-transparent to-transparent" />
                <div className="absolute top-3 right-3 w-8 h-8 border-t border-r border-[#E6D5B8]/60" />
                <div className="absolute bottom-3 left-3 w-8 h-8 border-b border-l border-[#E6D5B8]/60" />
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="text-[11px] tracking-luxury uppercase text-[#E6D5B8] mb-3">
                {FOUNDER.role}
              </div>
              <div className="flex flex-wrap items-baseline gap-3 mb-4">
                <h3 className="font-display text-3xl lg:text-5xl font-light text-[#F9F6F0]">
                  {FOUNDER.name}
                </h3>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span
                  data-testid="founder-cedula-badge"
                  className="text-xs tracking-luxury uppercase px-3 py-1.5 bg-[#E6D5B8]/10 text-[#E6D5B8] border border-[#E6D5B8]/30 rounded-sm"
                >
                  {FOUNDER.cedula} · {FOUNDER.cedulaNumber}
                </span>
                <span
                  data-testid="founder-mdrt-badge"
                  className="text-xs tracking-luxury uppercase px-3 py-1.5 bg-[#3D1565]/30 text-[#F9F6F0] border border-[#3D1565] rounded-sm flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 bg-[#E6D5B8] rounded-full" />
                  MDRT Member · {BRAND.mdrtYear}
                </span>
                <span
                  data-testid="founder-amasfac-badge"
                  className="text-xs tracking-luxury uppercase px-3 py-1.5 bg-[#3D1565]/30 text-[#F9F6F0] border border-[#3D1565] rounded-sm flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 bg-[#E6D5B8] rounded-full" />
                  Miembro AMASFAC
                </span>
                <span
                  data-testid="founder-canacintra-badge"
                  className="text-xs tracking-luxury uppercase px-3 py-1.5 bg-[#3D1565]/30 text-[#F9F6F0] border border-[#3D1565] rounded-sm flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 bg-[#E6D5B8] rounded-full" />
                  Miembro CANACINTRA
                </span>
              </div>
              <p className="text-sm lg:text-base text-[#F9F6F0]/75 leading-relaxed font-light max-w-3xl">
                {FOUNDER.bio}
              </p>

              {/* Credentials & affiliations */}
              {FOUNDER.credentials && FOUNDER.credentials.length > 0 && (
                <div
                  data-testid="founder-credentials"
                  className="mt-8 pt-8 border-t border-white/10"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="h-px w-7 bg-[#E6D5B8]" />
                    <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
                      Formación
                    </span>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                    {FOUNDER.credentials.map((c, i) => {
                      const logoSrc = c.logo ? BRAND[c.logo] : null;
                      return (
                        <li
                          key={c.name + c.institution}
                          data-testid={`founder-credential-${i}`}
                          className="group flex items-start gap-3"
                        >
                          {logoSrc ? (
                            <div className="shrink-0 w-9 h-9 flex items-center justify-center bg-white/95 rounded-sm p-1">
                              <img
                                src={logoSrc}
                                alt={c.institution}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          ) : (
                            <div className="shrink-0 mt-1.5">
                              <span
                                className="block w-1.5 h-1.5 bg-[#E6D5B8] rounded-full"
                                aria-hidden="true"
                              />
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="text-[10px] tracking-luxury uppercase text-[#A397B4]">
                              {c.type}
                            </div>
                            <div className="font-display text-base text-[#F9F6F0] leading-snug mt-0.5">
                              {c.name}
                            </div>
                            <div className="text-[11px] text-[#E6D5B8]/85 mt-1">
                              {c.institution}
                              {c.detail && (
                                <span className="text-[#A397B4]/85">
                                  {" · "}
                                  {c.detail}
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </article>

        {/* Team grid */}
        <div
          data-testid="advisors-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/10 border border-white/10"
        >
          {ADVISORS.filter((a) => a.name !== FOUNDER.name).map((a, idx) => (
            <button
              key={a.name}
              type="button"
              data-testid={`advisor-card-${idx}`}
              onClick={() => setSelected(a)}
              disabled={!a.photo}
              className="text-left bg-[#0B0410] hover:bg-[#150A1F] transition-colors duration-500 p-6 lg:p-7 flex items-center gap-5 group disabled:cursor-default enabled:cursor-pointer"
            >
              {a.photo ? (
                <div
                  className="relative w-14 h-14 shrink-0 rounded-sm overflow-hidden border border-[#3D1565] group-hover:border-[#E6D5B8]/60 transition-colors"
                  data-testid={`advisor-photo-${idx}`}
                >
                  <img
                    src={a.photo}
                    alt={a.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#0B0410]/0 group-hover:bg-[#0B0410]/40 flex items-center justify-center transition-colors duration-500">
                    <ZoomIn
                      size={16}
                      className="text-[#E6D5B8] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-14 h-14 flex items-center justify-center bg-[#150A1F] border border-[#2A0A4A] rounded-sm shrink-0">
                  <span className="font-display text-lg text-[#E6D5B8]">
                    {initialsOf(a.name)}
                  </span>
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-display text-lg text-[#F9F6F0] leading-tight group-enabled:group-hover:text-[#E6D5B8] transition-colors">
                  {a.name}
                </div>
                <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mt-2">
                  {cedulaLabel(a.cedula)} · CNSF
                  {a.cedulaNumber ? (
                    <span className="text-[#E6D5B8]"> · {a.cedulaNumber}</span>
                  ) : null}
                </div>
              </div>
            </button>
          ))}
        </div>

        <p className="mt-10 text-xs text-[#A397B4]/80 max-w-3xl">
          Cédula A: agente de seguros persona física. Cédula A1: vinculada
          también a productos de ahorro / inversión. Cédula B: agente apoderado
          para vincular a varias compañías. Todas vigentes ante la Comisión
          Nacional de Seguros y Fianzas (CNSF).
        </p>

        {/* Advisor portal access — for internal advisors */}
        <div
          className="mt-14 relative overflow-hidden rounded-sm border border-[#E6D5B8]/25"
          data-testid="advisor-portal-banner"
          style={{
            background:
              "linear-gradient(135deg, #1A0E2A 0%, #2A1248 45%, #150A1F 100%)",
          }}
        >
          {/* Hex pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.18] pointer-events-none"
            aria-hidden="true"
          >
            <svg width="100%" height="100%">
              <defs>
                <pattern
                  id="advisorPortalHex"
                  width="70"
                  height="121"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M35 0 L70 20 L70 60 L35 80 L0 60 L0 20 Z"
                    fill="none"
                    stroke="#E6D5B8"
                    strokeWidth="0.7"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#advisorPortalHex)" />
            </svg>
          </div>

          {/* Champagne ambient glow */}
          <div
            className="absolute -right-24 -top-24 w-72 h-72 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(230,213,184,0.22) 0%, rgba(230,213,184,0) 70%)",
            }}
            aria-hidden="true"
          />

          {/* Large hex with lock symbol */}
          <div
            className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 hidden md:block pointer-events-none opacity-[0.12]"
            aria-hidden="true"
          >
            <svg viewBox="0 0 200 230" className="w-44 h-44 lg:w-56 lg:h-56">
              <path
                d="M100 5 L195 60 L195 170 L100 225 L5 170 L5 60 Z"
                fill="none"
                stroke="#E6D5B8"
                strokeWidth="1.5"
              />
            </svg>
          </div>

          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8 px-6 sm:px-10 lg:px-14 py-10 lg:py-14">
            <div className="flex items-start gap-5 max-w-xl">
              {/* Hex icon with lock */}
              <div
                className="shrink-0 relative flex items-center justify-center w-16 h-16 lg:w-20 lg:h-20"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 100 115"
                  className="absolute inset-0 w-full h-full"
                >
                  <defs>
                    <linearGradient id="advPortalHexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3D1565" />
                      <stop offset="100%" stopColor="#5e23a3" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M50 2 L98 30 L98 85 L50 113 L2 85 L2 30 Z"
                    fill="url(#advPortalHexGrad)"
                    stroke="#E6D5B8"
                    strokeWidth="1.2"
                  />
                </svg>
                <Lock
                  size={26}
                  className="relative text-[#E6D5B8]"
                  strokeWidth={1.5}
                />
              </div>

              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-px w-8 bg-[#E6D5B8]" />
                  <span className="text-[10px] tracking-luxury uppercase text-[#E6D5B8] font-medium">
                    Portal interno · Solo asesores
                  </span>
                </div>
                <h3 className="font-display text-2xl lg:text-3xl text-[#F9F6F0] font-light leading-tight">
                  ¿Eres asesor de{" "}
                  <span className="italic text-[#E6D5B8]">EMB</span>?
                </h3>

                {/* Active advisors counter */}
                <div
                  className="mt-4 inline-flex items-center gap-2.5"
                  data-testid="active-advisors-counter"
                >
                  <span className="relative flex h-2 w-2" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-[#7FE3A1] opacity-60 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7FE3A1]" />
                  </span>
                  <span className="text-[11px] tracking-[0.18em] uppercase text-[#F9F6F0]/70 font-medium">
                    {ADVISORS.length}{" "}
                    <span className="text-[#F9F6F0]/50">asesores activos</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
              <a
                href="https://sales-tracker-hub-8.emergent.host/login"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="advisor-portal-login"
                className="group inline-flex items-center gap-3 bg-[#E6D5B8] hover:bg-[#FBEFD3] text-[#0B0410] px-7 py-4 rounded-sm transition-all duration-300 hover:-translate-y-0.5 shadow-[0_8px_28px_-6px_rgba(230,213,184,0.4)]"
              >
                <span className="text-xs tracking-luxury uppercase font-semibold">
                  Acceso asesores
                </span>
                <span
                  aria-hidden="true"
                  className="transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
              </a>

              {/* Secondary CTA → recruitment */}
              <a
                href="#unete"
                data-testid="advisor-join-cta"
                className="group inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-[#F9F6F0]/55 hover:text-[#E6D5B8] transition-colors duration-300"
              >
                <span>¿Aún no eres asesor?</span>
                <span className="font-semibold text-[#F9F6F0]/80 group-hover:text-[#E6D5B8] underline decoration-[#E6D5B8]/30 underline-offset-4 group-hover:decoration-[#E6D5B8]">
                  Únete
                </span>
                <span
                  aria-hidden="true"
                  className="transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Advisor lightbox dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent
          data-testid="advisor-dialog"
          className="bg-[#0B0410] text-[#F9F6F0] border-[#3D1565] rounded-sm p-0 max-w-3xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden overscroll-contain [&>button.absolute]:hidden"
        >
          {selected ? (
            <>
              {/* Sticky close bar — always visible while the user scrolls the modal content */}
              <div
                className="sticky top-0 z-30 flex justify-between items-start p-3 pointer-events-none"
                aria-hidden="false"
              >
                <button
                  type="button"
                  onClick={closeDialog}
                  data-testid="advisor-dialog-back"
                  aria-label="Regresar al inicio"
                  className="pointer-events-auto inline-flex items-center gap-2 bg-[#0B0410]/90 hover:bg-[#3D1565] border border-[#E6D5B8]/40 hover:border-[#E6D5B8] text-[#E6D5B8] rounded-sm backdrop-blur-sm transition-all duration-300 px-3 py-2 shadow-lg"
                >
                  <ArrowLeft size={16} strokeWidth={2.2} />
                  <span className="text-[10px] tracking-luxury uppercase">
                    Regresar
                  </span>
                </button>
                <button
                  type="button"
                  onClick={closeDialog}
                  data-testid="advisor-dialog-x"
                  aria-label="Cerrar"
                  className="pointer-events-auto w-11 h-11 flex items-center justify-center bg-[#0B0410]/90 hover:bg-[#3D1565] border border-[#E6D5B8]/40 hover:border-[#E6D5B8] text-[#E6D5B8] rounded-sm backdrop-blur-sm transition-all duration-300 shadow-lg"
                >
                  <X size={20} strokeWidth={2.2} />
                </button>
              </div>

              <div className="grid md:grid-cols-2 relative -mt-[68px]">
                <div className="relative aspect-square md:aspect-auto md:min-h-[520px] max-h-[60vh] md:max-h-none">
                  <img
                    src={selected.photo}
                    alt={selected.name}
                    data-testid="advisor-dialog-photo"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0410]/40 via-transparent to-transparent" />
                  <div className="absolute top-4 left-4 w-10 h-10 border-t border-l border-[#E6D5B8]/60" />
                  <div className="absolute bottom-4 right-4 w-10 h-10 border-b border-r border-[#E6D5B8]/60" />
                </div>
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <DialogTitle asChild>
                  <h3
                    data-testid="advisor-dialog-name"
                    className="font-display text-3xl lg:text-4xl font-light leading-tight"
                  >
                    {selected.name}
                  </h3>
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Información profesional del asesor {selected.name}
                </DialogDescription>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <span
                    data-testid="advisor-dialog-cedula"
                    className="text-xs tracking-luxury uppercase px-3 py-1.5 bg-[#E6D5B8]/10 text-[#E6D5B8] border border-[#E6D5B8]/30 rounded-sm"
                  >
                    {cedulaLabel(selected.cedula)} · CNSF
                  </span>
                  {selected.cedulaNumber ? (
                    <span
                      data-testid="advisor-dialog-cedula-number"
                      className="text-xs tracking-luxury uppercase px-3 py-1.5 bg-[#3D1565]/30 text-[#F9F6F0] border border-[#3D1565] rounded-sm"
                    >
                      No. {selected.cedulaNumber}
                    </span>
                  ) : null}
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 space-y-4">
                  <div>
                    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mb-1">
                      Firma
                    </div>
                    <div className="text-sm text-[#F9F6F0]/90 font-light">
                      EMB Consulting Group · Promotoría Allianz {BRAND.allianzPromotoria}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mb-1">
                      Especialidad
                    </div>
                    <div className="text-sm text-[#F9F6F0]/90 font-light">
                      Asesoría en ahorro, inversión y protección
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-luxury uppercase text-[#A397B4] mb-1">
                      Marco regulatorio
                    </div>
                    <div className="text-sm text-[#F9F6F0]/90 font-light">
                      Cédula vigente ante la Comisión Nacional de Seguros y
                      Fianzas (CNSF)
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  data-testid="advisor-dialog-cta"
                  onClick={() => {
                    try {
                      sessionStorage.setItem(
                        "emb:selectedAdvisor",
                        selected.name
                      );
                    } catch (_) {}
                    window.dispatchEvent(
                      new CustomEvent("emb:select-advisor", {
                        detail: { advisorName: selected.name },
                      })
                    );
                    closeDialog();
                    setTimeout(() => {
                      const el = document.getElementById("contacto");
                      if (el)
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 250);
                  }}
                  className="mt-8 inline-flex items-center justify-center bg-[#3D1565] hover:bg-[#4a1c79] text-white px-6 py-3 rounded-sm transition-all duration-300 text-xs tracking-luxury uppercase"
                >
                  Agendar asesoría con {selected.name}
                </button>
                <button
                  type="button"
                  onClick={closeDialog}
                  data-testid="advisor-dialog-close"
                  className="mt-4 self-start text-[11px] tracking-luxury uppercase text-[#A397B4] hover:text-[#E6D5B8] transition-colors duration-300 flex items-center gap-2"
                >
                  <span className="inline-block w-6 h-px bg-current" />
                  Volver a inicio
                </button>
              </div>
            </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Advisors;
